import { addtionalBuildingTypes, Map } from '@/widgets/map/ui/Map';
import { MainLayout } from '@/shared/layouts/ui/MainLayout';
import * as React from 'react';
import { KnowledgeMapCard } from '@/shared/ui/Card/KnowledgeMapCard';
import { SwiperSlide } from 'swiper/react';
import { Select } from '@/shared/ui/Select/Select';
import { Input } from '@/shared/ui/Input/Input';
import { IconButton } from '@/shared/ui/Button/IconButton';
import SearchIcon from '@/assets/icons/searchBlack.svg?react';
import { CustomCheckbox } from '@/shared/ui/Input/CustomCheckbox';
import { useLocation } from 'react-router-dom';
import { Button } from '@/shared/ui/Button';
import { useAppSelector } from '@/app/store/hooks';
import { selectBuildingCategories } from '@/entities/home_content/model/selectors';
import { filterDataType } from '@/entities/knowledge/model/types';
import {
  makeSelectFilteredBuildings,
  selectBuildingsError,
  selectBuildingsIsLoading,
} from '@/entities/knowledge/model/selectors';
import { MapSlider } from '@/widgets/slider/ui/MapSlider';
import { StateMessage } from '@/shared/ui/StateMessage';
import { Feedback } from '@/features/feedback/ui/Feedback';

const floorsList = [
  { name: 'до 5 этажей', value: 5 },
  { name: 'до 10 этажей', value: 10 },
  { name: 'до 20 этажей', value: 20 },
  { name: 'до 30 этажей', value: 30 },
  { name: 'до 50 этажей', value: 50 },
  { name: 'до 100 этажей', value: 100 },
];

const ariaList = [
  { name: 'до 1000 кв.м.', value: 1000 },
  { name: 'до 2000 кв.м.', value: 2000 },
  { name: 'до 3000 кв.м.', value: 3000 },
  { name: 'до 5000 кв.м.', value: 5000 },
  { name: 'до 10000 кв.м.', value: 10000 },
  { name: 'до 15000 кв.м.', value: 15000 },
];

const priceList = [
  { name: 'до 200 млн ₽', value: 200000000 },
  { name: 'до 400 млн ₽', value: 400000000 },
  { name: 'до 600 млн ₽', value: 600000000 },
  { name: 'до 1 млрд ₽', value: 1000000000 },
  { name: 'до 2 млрд ₽', value: 2000000000 },
  { name: 'до 5 млрд ₽', value: 5000000000 },
];

export default function MapPage() {
  const location = useLocation();

  const queryParams = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const direction = queryParams.get('direction');
  const priceFrom = queryParams.get('price_from');
  const priceTo = queryParams.get('price_to');

  const categories = useAppSelector(selectBuildingCategories);

  const directions = React.useMemo(
    () =>
      (categories ?? []).map((item) => ({
        name: item.title,
        value: item.id,
      })),
    [categories]
  );

  const [filterData, setFilterData] = React.useState<filterDataType>({
    direction: null,
    price_from: null,
    price_to: null,
    aria_to: null,
    floor_to: null,
    parking: false,
  });

  const [visibleCount, setVisibleCount] = React.useState(10);

  const [searchText, setSearchText] = React.useState<string>('');

  const selectFiltered = React.useMemo(makeSelectFilteredBuildings, []);
  let filteredBuildings = useAppSelector((state) =>
    selectFiltered(state, filterData)
  );
  const buildingsLoading = useAppSelector(selectBuildingsIsLoading);
  const buildingsError = useAppSelector(selectBuildingsError);

  const searchRef = React.useRef<HTMLInputElement>(null);

  const filteredBySearch = React.useMemo(() => {
    if (!searchText.trim()) return filteredBuildings;

    const lowerSearch = searchText.toLowerCase();

    return filteredBuildings.filter((building) => {
      return (
        building.name.toLowerCase().includes(lowerSearch) ||
        building.address.toLowerCase().includes(lowerSearch) ||
        building.district.toLowerCase().includes(lowerSearch) ||
        building.postalCode.toLowerCase().includes(lowerSearch)
      );
    });
  }, [searchText, filteredBuildings]);

  React.useEffect(() => {
    const newDirection = direction || null;
    const newPriceFrom = priceFrom ? Number(priceFrom) : null;
    const newPriceTo = priceTo ? Number(priceTo) : null;

    setFilterData((prev) => {
      if (
        prev.direction === newDirection &&
        prev.price_from === newPriceFrom &&
        prev.price_to === newPriceTo
      )
        return prev;

      return {
        ...prev,
        direction: newDirection,
        price_from: newPriceFrom,
        price_to: newPriceTo,
      };
    });
  }, [direction, priceFrom, priceTo]);

  const handleSelectChange = (
    name: string,
    value: string | number | boolean
  ) => {
    if (searchText !== '') setSearchText('');
    setFilterData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearchBtn = () => {
    const text = searchRef.current?.value || '';
    setSearchText(text);
  };

  const handleResetFilter = () => {
    if (searchText !== '') {
      setSearchText('');
      if (searchRef.current) {
        searchRef.current.value = '';
      }
    }

    setFilterData({
      direction: null,
      price_from: null,
      price_to: null,
      aria_to: null,
      floor_to: null,
      parking: false,
    });
  };

  return (
    <MainLayout
      wrClassName="bg-[#F4F4F4]"
      className="mx-0 w-full max-w-none px-0 xl:px-0"
    >
      <section className="w-full py-[20px]">
        <div className="container flex flex-col gap-[12px] px-[10px] md:gap-[16px] md:px-[32px] xl:gap-[20px] 2xl:px-[180px] 3xl:px-[290px]">
          <div className="flex w-full flex-col gap-[12px] sm:flex-row sm:gap-[16px]">
            <div className="flex flex-row gap-[12px] sm:w-full sm:gap-[16px]">
              <div className="shadow-cls flex h-[44px] items-center gap-[4px] rounded-[15px] bg-[#F4F4F4] p-[10px] outline-none sm:flex-1 md:px-[20px]">
                <SearchIcon className="h-[15px] w-[15px] stroke-[white]" />
                <Input
                  ref={searchRef}
                  className="h-full w-[265px] rounded-none border-none bg-transparent p-0 text-[14px] font-[400] sm:w-full md:text-[16px]"
                  fullWidth={true}
                  placeholder="Введите адрес, район или почтовый индекс"
                />
              </div>
              <IconButton
                icon={<SearchIcon className="h-[20px] w-[20px]" />}
                className="shadow-cls h-[44px] w-[44px] rounded-[15px] bg-[#F4F4F4]"
                onClick={handleSearchBtn}
              />
            </div>
            <Button
              text="Сбросить фильтры"
              className="shadow-cls h-[34px] rounded-[15px] px-[24px] text-[14px] font-[400] sm:h-[44px] md:text-[16px]"
              onClick={handleResetFilter}
            />
          </div>

          <div className="flex flex-col gap-[12px] md:gap-[16px] xl:flex-row xl:gap-[20px]">
            <div className="flex flex-col gap-[12px] sm:flex-row xl:w-[40%] xl:gap-[20px]">
              <Select
                list={directions}
                current={filterData.direction}
                wrClassName="w-auto xl:flex-1"
                className="h-[40px] w-[245px] rounded-[15px] md:h-[44px] xl:w-full"
                name="direction"
                placeholder="Назначение"
                handleChange={handleSelectChange}
                isBlack="true"
              />

              <Select
                list={priceList}
                current={filterData.price_to}
                wrClassName="w-auto xl:flex-1"
                className="h-[40px] w-[245px] rounded-[15px]  md:h-[44px] xl:w-full"
                placeholder="Цены"
                name="price_to"
                handleChange={handleSelectChange}
                isBlack="true"
              />
            </div>

            <div className="flex flex-wrap gap-[12px] md:gap-[16px] xl:w-[60%] xl:gap-[20px]">
              <Select
                list={ariaList}
                current={filterData.aria_to}
                wrClassName="w-auto xl:flex-1"
                className="shadow-cls h-[40px] w-[170px] rounded-[15px] md:h-[44px] md:w-[245px] xl:w-full"
                placeholder="Площадь"
                name="aria_to"
                handleChange={handleSelectChange}
              />
              <Select
                list={floorsList}
                current={filterData.floor_to}
                placeholder="Этажность"
                className="shadow-cls h-[40px] w-[170px] rounded-[15px] md:h-[44px] md:w-[245px] xl:w-full"
                wrClassName="w-auto xl:flex-1"
                name="floor_to"
                handleChange={handleSelectChange}
              />
              <CustomCheckbox
                name="parking"
                placeholder="Паркинг"
                className="shadow-cls h-[40px] w-[170px] rounded-[15px] md:h-[44px] xl:w-auto xl:flex-1"
                value={filterData.parking}
                handleChange={handleSelectChange}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full">
        <Map buildings={filteredBySearch} />
      </section>

      <section className="w-full  pt-[32px] md:pt-[40px]">
        <div className="container flex flex-col gap-[20px] px-[10px] md:px-[32px] 2xl:px-[180px] 3xl:px-[290px]">
          <div className="flex flex-wrap justify-center gap-[20px] md:gap-[32px] xl:gap-[40px]">
            {filteredBySearch.slice(0, visibleCount).map((building) => (
              <KnowledgeMapCard
                key={building.id}
                building={building}
                additional={
                  addtionalBuildingTypes[
                    building.direction as keyof typeof addtionalBuildingTypes
                  ]
                }
                wrClassName="w-full sm:w-min"
                className="min-h-full w-[inherit] max-w-full sm:w-full sm:min-w-[306px] sm:max-w-[360px]"
              />
            ))}
          </div>

          {visibleCount < filteredBySearch.length && (
            <div className="flex items-center justify-center">
              <Button
                text={`Показать еще 10 объектов`}
                className="shadow-cls bg-white px-[52px] py-[12px] text-[14px] leading-[0.97em] md:py-[16px] md:text-[16px]"
                onClick={() => setVisibleCount((prev) => prev + 10)}
              />
            </div>
          )}
        </div>
      </section>

      <section className="w-full  py-[40px]">
        <div className="container flex flex-col px-[10px] md:px-[32px] 2xl:px-[180px] 3xl:px-[290px]">
          <Feedback />
        </div>
      </section>
    </MainLayout>
  );
}
